import React from 'react';
import {
	Box,
	CircularProgress,
	IconButton,
	Modal,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TicketAttachment } from '../../types/ticket';
import { getAttachmentPreviewUrl } from '../../utils/getAttachmentPreviewUrl';

interface AttachmentImageGalleryProps {
	attachments: TicketAttachment[];
}

const GalleryThumbnail: React.FC<{
	img: TicketAttachment;
	onClick: () => void;
}> = ({ img, onClick }) => {
	const [failed, setFailed] = React.useState(false);

	return (
		<Box
			onClick={failed ? undefined : onClick}
			role="button"
			aria-label={`Preview ${img.Name}`}
			sx={{
				width: 96,
				height: 96,
				flexShrink: 0,
				borderRadius: 2,
				overflow: 'hidden',
				border: 1,
				borderColor: 'divider',
				cursor: failed ? 'default' : 'pointer',
				bgcolor: 'background.default',
				transition: 'transform 120ms ease',
				'&:hover': { transform: failed ? 'none' : 'scale(1.02)' },
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				p: failed ? 0.5 : 0,
			}}
		>
			{failed ? (
				<Typography
					variant="caption"
					color="text.secondary"
					textAlign="center"
				>
					Couldn't fetch image
				</Typography>
			) : (
				<Box
					component="img"
					src={getAttachmentPreviewUrl(img.AttachmentID)}
					alt={img.Name}
					onError={() => setFailed(true)}
					sx={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						display: 'block',
					}}
				/>
			)}
		</Box>
	);
};

const AttachmentImageGallery: React.FC<AttachmentImageGalleryProps> = ({
	attachments,
}) => {
	const images = React.useMemo(
		() =>
			(attachments ?? []).filter((a) =>
				a.ContentType?.toLowerCase().includes('image'),
			),
		[attachments],
	);

	const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(false);

	if (images.length === 0) return null;

	const open = (idx: number) => {
		setActiveIndex(idx);
		setLoading(true);
		setError(false);
	};

	const close = () => setActiveIndex(null);

	const step = (delta: number) => {
		if (activeIndex === null) return;
		const next = (activeIndex + delta + images.length) % images.length;
		setActiveIndex(next);
		setLoading(true);
		setError(false);
	};

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					gap: 1,
					overflowX: 'auto',
					py: 1,
					// Hide scrollbar for a cleaner look on touch devices.
					'&::-webkit-scrollbar': { display: 'none' },
					scrollbarWidth: 'none',
				}}
			>
				{images.map((img, idx) => (
					<GalleryThumbnail
						key={img.AttachmentID}
						img={img}
						onClick={() => open(idx)}
					/>
				))}
			</Box>

			<Modal open={activeIndex !== null} onClose={close}>
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						bgcolor: 'rgba(0, 0, 0, 0.92)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						outline: 'none',
					}}
				>
					<IconButton
						onClick={close}
						aria-label="Close preview"
						sx={{
							position: 'absolute',
							top: 'calc(8px + env(safe-area-inset-top))',
							right: 8,
							color: 'common.white',
							bgcolor: 'rgba(0, 0, 0, 0.4)',
							'&:hover': { bgcolor: 'rgba(0, 0, 0, 0.6)' },
						}}
					>
						<CloseIcon />
					</IconButton>

					{images.length > 1 && (
						<>
							<IconButton
								onClick={() => step(-1)}
								aria-label="Previous image"
								sx={{
									position: 'absolute',
									left: 8,
									top: '50%',
									transform: 'translateY(-50%)',
									color: 'common.white',
									bgcolor: 'rgba(0, 0, 0, 0.4)',
									'&:hover': { bgcolor: 'rgba(0, 0, 0, 0.6)' },
								}}
							>
								<ChevronLeftIcon />
							</IconButton>
							<IconButton
								onClick={() => step(1)}
								aria-label="Next image"
								sx={{
									position: 'absolute',
									right: 8,
									top: '50%',
									transform: 'translateY(-50%)',
									color: 'common.white',
									bgcolor: 'rgba(0, 0, 0, 0.4)',
									'&:hover': { bgcolor: 'rgba(0, 0, 0, 0.6)' },
								}}
							>
								<ChevronRightIcon />
							</IconButton>
						</>
					)}

					{loading && (
						<CircularProgress sx={{ color: 'common.white' }} />
					)}

					{error && (
						<Typography color="error">
							Couldn't fetch image
						</Typography>
					)}

					{activeIndex !== null && !error && (
						<Box
							component="img"
							src={getAttachmentPreviewUrl(images[activeIndex].AttachmentID)}
							alt={images[activeIndex].Name}
							onLoad={() => setLoading(false)}
							onError={() => {
								setLoading(false);
								setError(true);
							}}
							sx={{
								maxWidth: '95vw',
								maxHeight: '90vh',
								objectFit: 'contain',
								display: loading ? 'none' : 'block',
								borderRadius: 1,
							}}
						/>
					)}

					{activeIndex !== null && images.length > 1 && (
						<Typography
							sx={{
								position: 'absolute',
								bottom: 'calc(16px + env(safe-area-inset-bottom))',
								left: '50%',
								transform: 'translateX(-50%)',
								color: 'common.white',
								bgcolor: 'rgba(0, 0, 0, 0.4)',
								px: 1.5,
								py: 0.5,
								borderRadius: 2,
								fontSize: 12,
							}}
						>
							{activeIndex + 1} / {images.length}
						</Typography>
					)}
				</Box>
			</Modal>
		</>
	);
};

export default AttachmentImageGallery;
